// Fetches a room's full state (config, requests, played list) on a polling
// interval. Used by the DANCER view — uses REST so we don't open a websocket.

import { useEffect, useState } from 'react'
import { pollNode } from '../firebase/rest.js'

const POLL_INTERVAL_MS = 12_000 // tuned for Spark bandwidth budget

/**
 * Returns:
 *   { venue, rooms, room, requests, played, loading, error }
 * - `room` is the active room's config (sugar for rooms[roomId])
 * - `rooms` is the full map of all rooms — needed by the dancer's request form
 *   so it can show both rooms as options with their availability state
 * - `requests` and `played` are arrays sorted in display order
 */
export function useRoomData(roomId) {
  const [venue, setVenue] = useState({ open: false, opened_at: null, closed_at: null })
  const [rooms, setRooms] = useState({})
  const [requests, setRequests] = useState([])
  const [played, setPlayed] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!roomId) return
    setLoading(true)

    // We poll the WHOLE root because the request list includes both rooms.
    // Filtering by room happens client-side. Data is small (text only,
    // kilobytes). If we ever outgrow this we can split into per-room
    // subtrees and poll each separately.
    const stop = pollNode(
      '/',
      POLL_INTERVAL_MS,
      (data) => {
        const safe = data || {}
        setVenue({ open: false, opened_at: null, closed_at: null, ...(safe.venue || {}) })
        setRooms(safe.rooms || {})

        const reqs = Object.entries(safe.requests || {})
          .map(([id, r]) => ({
            id,
            ...r,
            voteCount: r.votes ? Object.keys(r.votes).length : 0,
          }))
        setRequests(reqs)

        const pl = Object.entries(safe.played_tonight || {})
          .map(([id, p]) => ({ id, ...p }))
          .sort((a, b) => (b.played_at || 0) - (a.played_at || 0))
        setPlayed(pl)

        setLoading(false)
        setError(null)
      },
      (err) => {
        setError(err)
        setLoading(false)
      },
    )
    return stop
  }, [roomId])

  const room = rooms[roomId] || null
  return { venue, rooms, room, requests, played, loading, error }
}
